import { Capacitor } from '@capacitor/core'
import { ILabelDataHandler } from '../../data-handlers/label/ILabelDataHandler'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import Notification from '../../entities/notification/Notification'
import { store } from '../../services/store'
import { setNotification } from '../../services/store/actions/notifications/NotificationsAction'
import FileFormats from '../../constants/FileFormats'
import { IntlService } from '../../services/intl/IntlService'
import toastMessages from '../../services/intl/ToastMessages'
import { loggerService } from '../../services/logger/loggerService'
import { isNativePdfPrintingAvailable, printPdfNatively } from '../../services/print/nativePdfPrinter'
import LabelPrinter, { isLabelPrinterAvailable } from '../../services/print/labelPrinter'
import PrinterTransports from '../../constants/PrinterTransports'
import {
  getPreferredBluetoothName,
  getPreferredUsbDeviceKey,
  resolveTransport,
} from '../../services/printer/printerSettings'
export class LabelRepository {
  handler: ILabelDataHandler
  _BTPrinter: any

  constructor(handler: ILabelDataHandler) {
    this.handler = handler

    if('BTPrinter' in window) {
      this._BTPrinter = window.BTPrinter
    }
  }

  async createBatch(products: OrderProduct[]): Promise<void> {
    const label = await this.handler.createBatch(products)

    // Awaited so a print failure rejects this call. Without it the epic dispatches
    // requestPrintSuccess for a label that never came out, and the rejection
    // surfaces only as an unhandled promise.
    await this.printDocument(products[0].order.orderId, label, products[0].itemId)
  }

  remove(product: OrderProduct): Promise<void> {
    return this.handler.remove(product)
  }

  removeBatch(products: OrderProduct[]): Promise<void> {
    return this.handler.removeBatch(products)
  }

  async print(product: OrderProduct) {
    const label = await this.handler.create(product)
    await this.printDocument(product.order.orderId, label, product.itemId)
  }

  async rePrint(product: OrderProduct) {
    if (product.label) {
      await this.printDocument(product.order.orderId, product.label, product.itemId)
    } else {
      throw new Error('Attempting to reprint product with empty label')
    }
  }

  async printDocument(orderId: number, label: string, itemId: number) {
    try {
      const {fileContent, fileType} = await this.handler.getDocumentBase64ByLabel(orderId, label, itemId)

      // Validate base64 content before attempting to decode
      if (!fileContent || typeof fileContent !== 'string') {
        console.error('Invalid file content from server:', { fileContent, fileType, orderId, label, itemId })
        throw new Error(`Server returned invalid file content. Type: ${typeof fileContent}, Content: ${JSON.stringify(fileContent)?.substring(0, 100)}`)
      }

      let decodedFileContent: string
      try {
        decodedFileContent = atob(fileContent)
      } catch (base64Error) {
        const errorMessage = base64Error instanceof Error ? base64Error.message : 'Unknown error'
        throw new Error(`Invalid base64 data: ${errorMessage}`)
      }

      if (fileType === FileFormats.ZPL) {
        await this.printDocumentByZpl(fileContent, decodedFileContent)
      } else if (Capacitor.isNativePlatform()) {
        // The web PDF path is a dead end inside the Android WebView: window.open
        // hands a blob: URL to an external browser that cannot resolve it, and the
        // <a download> fallback needs a DownloadListener the WebView does not have.
        // Both fail silently. Pass the original base64 (not decodedFileContent) to
        // the native PrintManager bridge instead — Java re-decodes it itself.
        await this.printDocumentNatively(fileContent, label)
      } else {
        const documentUrl = await this.getDocumentUrl(decodedFileContent)
        this.printDocumentByUrl(documentUrl)
      }
    } catch (error) {
      console.error('Print document error:', error)
      // Escalate: the seller just failed to print a label. This catch re-throws and
      // sits above the invalid-content, base64 and getDocumentUrl failures, so one
      // report here covers the whole synchronous print path without duplicating.
      loggerService.error(error)
      // Re-throw the error so the epic can catch it and handle the loading state
      throw error
    }
  }

  async getDocumentUrl(fileContent: string): Promise<string> {
    try {
      const byteArray = Uint8Array.from(
        fileContent.split('').map(char => char.charCodeAt(0))
      )

      const documentBlob = new Blob([byteArray], { type: 'application/pdf' })

      return URL.createObjectURL(documentBlob)

    } catch (error) {
      console.error(error)
      throw new Error('Can`t get document url')
    }
  }

  setPrinterNotification(id: string, text: string) {
    store.dispatch(setNotification(new Notification({
      id,
      text,
    })))
  }

  /**
   * Native PDF printing lives in the APK (PdfPrinterPlugin) while this file ships over
   * OTA, so an older APK can receive a bundle that knows about it. Check availability
   * before calling rather than letting Capacitor reject with a generic "not
   * implemented" the seller cannot act on.
   */
  async printDocumentNatively(base64FileContent: string, label: string): Promise<void> {
    if (!isNativePdfPrintingAvailable()) {
      // Thrown rather than toasted-and-swallowed: nothing was printed, so the epic
      // must record a failure instead of dispatching requestPrintSuccess. It renders
      // this message as the toast.
      throw new Error(IntlService.formatMessage(toastMessages.nativePdfPrintUnavailable))
    }

    await printPdfNatively(base64FileContent, `label-${label}`)
  }

  /**
   * Sends ZPL over whichever transport is currently resolvable — a USB cable or
   * Bluetooth. Takes both encodings of the same label: the native plugin wants the
   * untouched base64 so the server's exact bytes survive the bridge, while the legacy
   * Cordova fallback below only accepts an already-decoded string.
   */
  async printDocumentByZpl(base64FileContent: string, decodedFileContent: string): Promise<void> {
    // Old APK, new OTA bundle: LabelPrinter ships in the APK but this file does not,
    // so a tablet can receive this code before it has the plugin. Falling back to the
    // Cordova plugin that shipped before it means an OTA update can never leave a
    // seller unable to print.
    if (!isLabelPrinterAvailable()) {
      this.printDocumentByLegacyBluetooth(decodedFileContent)
      return
    }

    try {
      const transport = await resolveTransport()

      if (transport === PrinterTransports.USB) {
        const usbDeviceKey = getPreferredUsbDeviceKey()

        await LabelPrinter.printUsb({
          base64: base64FileContent,
          // Omitted rather than passed as undefined, so the plugin applies its own
          // "first usable, preferring one already permitted" fallback.
          ...(usbDeviceKey ? { deviceKey: usbDeviceKey } : {}),
        })
        return
      }

      await this.ensureBluetoothPermission()

      const deviceName = getPreferredBluetoothName()
      await LabelPrinter.printBluetooth({
        base64: base64FileContent,
        // Omitted rather than passed as undefined: the plugin treats "no name" as
        // "use the first paired printer", which is the pre-selection behaviour.
        ...(deviceName ? { deviceName } : {}),
      })
    } catch (error) {
      throw this.toLocalizedPrintError(error)
    }
  }

  /**
   * BLUETOOTH_CONNECT is a runtime permission from Android 12 onward. The plugin
   * reports it granted on older releases, where the legacy install-time permission
   * covers it, so this is safe to call unconditionally.
   */
  private async ensureBluetoothPermission(): Promise<void> {
    const { bluetooth } = await LabelPrinter.checkPermissions()

    if (bluetooth === 'granted') {
      return
    }

    const { bluetooth: afterRequest } = await LabelPrinter.requestPermissions()

    if (afterRequest !== 'granted') {
      throw Object.assign(new Error('Bluetooth permission was not granted'), { code: 'PERMISSION_DENIED' })
    }
  }

  /**
   * Rewrites a plugin error code into a message the seller can act on.
   *
   * Deliberately returns an error to throw rather than dispatching a toast here:
   * requestPrintEpic already renders one from error.message on rejection, so toasting
   * as well would show two for a single failure. Swallowing the error instead is not
   * an option — the epic would then dispatch requestPrintSuccess for a label that
   * never came out.
   */
  private toLocalizedPrintError(error: unknown): Error {
    const code = (error as { code?: string })?.code

    const message = (() => {
      switch (code) {
        case 'PERMISSION_DENIED':
          return IntlService.formatMessage(toastMessages.printerPermissionDenied)
        case 'BLUETOOTH_DISABLED':
          return IntlService.formatMessage(toastMessages.bluetoothDisabled)
        case 'DEVICE_NOT_FOUND':
        case 'UNSUPPORTED_DEVICE':
          return IntlService.formatMessage(toastMessages.printerDeviceNotFound)
        default:
          // No recognised code: keep the original text, which carries the plugin's own
          // detail and is what Crashlytics needs to be useful.
          return (error as { message?: string })?.message
            || IntlService.formatMessage(toastMessages.printerNotConnected)
      }
    })()

    return Object.assign(new Error(message), { code, cause: error })
  }

  /**
   * The pre-LabelPrinter Bluetooth path, kept only so an OTA bundle landing on an
   * older APK still prints. It always uses the first paired device — printer
   * selection is not available here.
   */
  printDocumentByLegacyBluetooth(documentZpl: string) {
    const printer = this._BTPrinter

    // The Cordova plugin only exists in the native build. Now that ZPL is selectable
    // on every platform, a seller on web can reach here with no plugin loaded —
    // without this guard that is a bare TypeError inside printDocument's catch,
    // reported as an opaque crash rather than a fixable setting.
    if (!printer) {
      throw new Error(IntlService.formatMessage(toastMessages.bluetoothPrinterUnavailable))
    }

    printer.status((isActiveBluetooth: boolean) => {
      if (isActiveBluetooth) {
        printer.list((deviceInfo: Array<string>) => {
          const deviceName: string = deviceInfo[0]
          printer.connect((): void => {
            printer.connected((isPrinterConnected: boolean) => {
              if (isPrinterConnected) {
                printer.printText(() => {
                  setTimeout(() => {
                    printer.disconnect(() => {
                    }, () => {
                      this.setPrinterNotification('printer_not_disconnected', IntlService.formatMessage(toastMessages.printerNotDisconnected))
                    }, deviceName)
                  }, 1500)
                }, () => {
                  this.setPrinterNotification('text_printing_error', IntlService.formatMessage(toastMessages.textNotPrinting))
                }, documentZpl)
              } else {
                this.setPrinterNotification('printer_connection_error', IntlService.formatMessage(toastMessages.printerNotConnected))
              }
            })
          }, () => {
            this.setPrinterNotification('printer_connection_error', 'Printer is not connected')
          }, deviceName)
        })
      } else {
        this.setPrinterNotification('bluetooth_disabled', IntlService.formatMessage(toastMessages.bluetoothDisabled))
      }
    }, () => {
      this.setPrinterNotification('printer_status', IntlService.formatMessage(toastMessages.printerStatusUnknown))
    })
  }

  printDocumentByUrl(documentUrl: string): void {
    console.log('Attempting to print document URL:', documentUrl.substring(0, 100) + '...')

    try {
      const pdfWindow = window.open(documentUrl, '_blank', 'width=800,height=600')
      console.log('window.open result:', pdfWindow)

      if (pdfWindow) {
        console.log('PDF window opened successfully, focusing and printing...')
        pdfWindow.focus()

        // Wait a bit for the PDF to load before printing
        setTimeout(() => {
          try {
            pdfWindow.print()
            console.log('Print dialog should be open')
          } catch (printError) {
            console.error('Error calling print():', printError)
            // Fires inside a timeout, so it escapes every catch above it and would
            // otherwise be completely invisible — the seller just sees no dialog.
            loggerService.error(printError)
          }
        }, 500)
      } else {
        console.error('window.open returned null - popup blocked, trying download fallback')
        this.downloadPdfFallback(documentUrl)
        return // Don't throw error, fallback succeeded
      }
    } catch (error) {
      console.error('Error in printDocumentByUrl:', error)
      // Don't throw error if we have a fallback available
      console.log('Attempting download fallback due to error')
      this.downloadPdfFallback(documentUrl)
    } finally {
      // Don't revoke immediately, give time for the window to load
      setTimeout(() => {
        URL.revokeObjectURL(documentUrl)
      }, 2000)
    }
  }

  private downloadPdfFallback(documentUrl: string): void {
    console.log('Using download fallback for PDF')

    // Create a temporary download link
    const downloadLink = document.createElement('a')
    downloadLink.href = documentUrl
    downloadLink.download = `label-${Date.now()}.pdf`
    downloadLink.style.display = 'none'

    // Add to DOM, click, and remove
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)

    console.log('PDF download initiated')

    // Show notification to user
    this.setPrinterNotification('pdf_download', IntlService.formatMessage(toastMessages.pdfLabelDownloaded))
  }
}

declare global {
  interface Window { BTPrinter: any; }
}
